'use client';

import {
  Button,
  MenuWithItems,
  Popover,
  Spinner,
  Tooltip,
  BodyDefault,
  type MenuItemProps,
} from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';
import { Bug02Icon } from '@terra/ui-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  DEEP_SECURITY_TEST_CASE_SCAN_ENABLED_FLAG,
  DYNAMIC_APPLICATION_SCAN_AUTOMATION_ENABLED_FLAG,
} from '@terra/shared-contracts-feature-flags';
import {
  ACTIVE_STATUSES,
  useCrawlerSessions,
} from '../../hooks/useCrawlerSessions';
import { CrawlerMonitorPanel, CrawlerHistoryDrawer } from '../crawler';
import {
  AiDiscoveryHistoryDrawer,
  AiDiscoveryMonitorPanel,
} from '../ai-discovery';
import { useAiDiscoveryTriggerBadge } from '../ai-discovery/useAiDiscoveryTriggerBadge';
import { DeepSecurityScanPanel } from '../deep-security-scan';
import { DirectoryScanPanel } from '../directory-scan';
import { ErrorBoundary } from '../ErrorBoundary';
import { useCrawlerUnseenStore } from '../../store/crawlerUnseenStore';
import { useProjectStore } from '../../store/projectStore';
import { useSSE } from '../../app/providers/SseProvider';
import { CRAWLER_SESSION_STATUS_CHANGED_SSE_EVENT } from '@terra/shared-contracts-applications';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { TARGET_TYPE } from '../../utils/target-scope.utils';
import { useSourceCodeInfo } from '../../hooks/dashboard/useSourceCodeInfo';
import { buildDiscoveryMenuItems } from './crawlerStatusPopoverMenu.util';

type CrawlerStatusPopoverProps = {
  applicationId?: string;
  disabled?: boolean;
  isBackendOnly?: boolean;
  isDraft?: boolean;
};

type DiscoveryPanel =
  | 'crawler'
  | 'directory-scan'
  | 'deep-security-scan'
  | 'ai-discovery';

const NESTED_DISMISSABLE_LAYER_SELECTOR = [
  '[data-scope="select"][data-part="content"][data-state="open"]',
  '[data-scope="dialog"][data-part="content"][data-state="open"]',
  '[data-scope="menu"][data-part="content"][data-state="open"]',
].join(',');

export function CrawlerStatusPopover({
  applicationId,
  disabled,
  isBackendOnly,
  isDraft,
}: CrawlerStatusPopoverProps) {
  const t = useTranslations('crawler.topBarTrigger');
  const tDiscovery = useTranslations('discovery');
  const tAi = useTranslations('discovery.aiApplication');
  const targetType = useProjectStore((state) => state.targetType);
  const isAiApplication = targetType === TARGET_TYPE.AI_APPLICATION;
  const {
    isFeatureFlagEnabled: isDynamicApplicationScanEnabled,
    isFetched: isDynamicScanFlagFetched,
  } = useFeatureFlags(DYNAMIC_APPLICATION_SCAN_AUTOMATION_ENABLED_FLAG);
  const {
    isFeatureFlagEnabled: isDeepSecurityScanEnabled,
    isFetched: isDeepSecurityScanFlagFetched,
  } = useFeatureFlags(DEEP_SECURITY_TEST_CASE_SCAN_ENABLED_FLAG);
  const showDirectoryScan =
    !isDynamicScanFlagFetched || isDynamicApplicationScanEnabled;
  const showDeepSecurityScan =
    !isDeepSecurityScanFlagFetched || isDeepSecurityScanEnabled;
  const { data: sourceCodeInfo } = useSourceCodeInfo(applicationId ?? '');
  const isSourceCodeConnected = sourceCodeInfo?.isConnected;
  const [panel, setPanel] = useState<DiscoveryPanel | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyScope, setHistoryScope] = useState({
    applicationId,
    isAiApplication,
  });

  if (
    historyScope.applicationId !== applicationId ||
    historyScope.isAiApplication !== isAiApplication
  ) {
    setHistoryScope({ applicationId, isAiApplication });
    if (isHistoryOpen) {
      setIsHistoryOpen(false);
    }
  }

  const triggerAnchorRef = useRef<HTMLDivElement>(null);
  const isCrawlerOpen = panel === 'crawler';
  const isAiDiscoveryOpen = panel === 'ai-discovery';
  const isPanelOpen = panel !== null;
  const isMonitorOpenRef = useRef(isCrawlerOpen || isAiDiscoveryOpen);
  const isHistoryOpenRef = useRef(isHistoryOpen);
  const markTriggerUnseen = useCrawlerUnseenStore(
    (state) => state.markTriggerUnseen,
  );
  const markHistoryUnseen = useCrawlerUnseenStore(
    (state) => state.markHistoryUnseen,
  );
  const clearTriggerUnseen = useCrawlerUnseenStore(
    (state) => state.clearTriggerUnseen,
  );
  const clearHistoryUnseen = useCrawlerUnseenStore(
    (state) => state.clearHistoryUnseen,
  );
  const hasTriggerUnseen = useCrawlerUnseenStore((state) =>
    state.hasTriggerUnseen(applicationId),
  );
  const hasHistoryUnseen = useCrawlerUnseenStore((state) =>
    state.hasHistoryUnseen(applicationId),
  );
  const { subscribe } = useSSE();
  const { activeSessionCount, isLoading: isCrawlerLoading } =
    useCrawlerSessions({
      applicationId,
      enabled: !!applicationId && !isAiApplication,
    });
  const { badgeCount: aiLiveCount, isLoading: isAiBadgeLoading } =
    useAiDiscoveryTriggerBadge({
      applicationId,
      enabled: isAiApplication,
      isPanelOpen: isAiDiscoveryOpen,
      isHistoryOpen,
    });
  const badgeCount = isAiApplication ? aiLiveCount : activeSessionCount;
  const isLoading = isAiApplication ? isAiBadgeLoading : isCrawlerLoading;

  useEffect(() => {
    isMonitorOpenRef.current = isCrawlerOpen || isAiDiscoveryOpen;
  }, [isAiDiscoveryOpen, isCrawlerOpen]);

  useEffect(() => {
    isHistoryOpenRef.current = isHistoryOpen;
  }, [isHistoryOpen]);

  useEffect(() => {
    if (!applicationId || isAiApplication) {
      return;
    }

    return subscribe(
      CRAWLER_SESSION_STATUS_CHANGED_SSE_EVENT,
      (event) => {
        const isTerminal = !(ACTIVE_STATUSES as readonly string[]).includes(
          event.status,
        );

        if (!isTerminal) {
          return;
        }

        if (!isHistoryOpenRef.current) {
          markHistoryUnseen(applicationId);
        }

        if (!isMonitorOpenRef.current && !isHistoryOpenRef.current) {
          markTriggerUnseen(applicationId);
        }
      },
      { filter: (event) => event.targetId === applicationId },
    );
  }, [
    applicationId,
    isAiApplication,
    markHistoryUnseen,
    markTriggerUnseen,
    subscribe,
  ]);

  useEffect(() => {
    if ((isCrawlerOpen || isAiDiscoveryOpen) && applicationId) {
      clearTriggerUnseen(applicationId);
    }
  }, [applicationId, clearTriggerUnseen, isAiDiscoveryOpen, isCrawlerOpen]);

  useEffect(() => {
    if (isHistoryOpen && applicationId) {
      clearHistoryUnseen(applicationId);
      clearTriggerUnseen(applicationId);
    }
  }, [isHistoryOpen, applicationId, clearHistoryUnseen, clearTriggerUnseen]);

  const isDisabled = !!disabled || !!isDraft || !applicationId;

  const menuItems = useMemo<MenuItemProps[]>(
    () =>
      buildDiscoveryMenuItems({
        isAiApplication,
        isBackendOnly: !!isBackendOnly && !isAiApplication,
        isSourceCodeConnected,
        showDirectoryScan,
        showDeepSecurityScan,
        labels: {
          aiDiscovery: tDiscovery('aiDiscovery'),
          crawler: tDiscovery('crawler'),
          directoryScan: tDiscovery('directoryScan'),
          deepSecurityScan: tDiscovery('deepSecurityScan'),
        },
        onSelectAiDiscovery: () => setPanel('ai-discovery'),
        onSelectCrawler: () => setPanel('crawler'),
        onSelectDirectoryScan: () => setPanel('directory-scan'),
        onSelectDeepSecurityScan: () => setPanel('deep-security-scan'),
      }),
    [
      isAiApplication,
      isBackendOnly,
      isSourceCodeConnected,
      showDeepSecurityScan,
      showDirectoryScan,
      tDiscovery,
    ],
  );

  useEffect(() => {
    if (isAiApplication) {
      if (panel && panel !== 'ai-discovery') {
        setPanel(null);
      }

      return;
    }

    if (panel === 'ai-discovery') {
      setPanel(null);
    }
  }, [isAiApplication, panel]);

  useEffect(() => {
    if (
      panel === 'directory-scan' &&
      isDynamicScanFlagFetched &&
      !isDynamicApplicationScanEnabled
    ) {
      setPanel(null);
    }
  }, [panel, isDynamicScanFlagFetched, isDynamicApplicationScanEnabled]);

  useEffect(() => {
    if (
      panel === 'deep-security-scan' &&
      isDeepSecurityScanFlagFetched &&
      !isDeepSecurityScanEnabled
    ) {
      setPanel(null);
    }
  }, [panel, isDeepSecurityScanFlagFetched, isDeepSecurityScanEnabled]);

  useEffect(() => {
    if (panel === 'deep-security-scan' && isSourceCodeConnected === false) {
      setPanel(null);
    }
  }, [panel, isSourceCodeConnected]);

  if (isLoading && !applicationId) {
    return (
      <Button
        variant="ghost"
        size="xs"
        disabled
        aria-label={t('triggeringAriaLabel')}
      >
        <Spinner size="sm" />
      </Button>
    );
  }

  const triggerLabel = isAiApplication
    ? tAi('statusAriaLabel')
    : activeSessionCount > 0
      ? t('crawlingInProgress')
      : t('startCrawling');
  const openHistory = () => {
    setIsHistoryOpen(true);
    setPanel(null);
  };

  return (
    <>
      <Popover.Root
        open={isPanelOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            setPanel(null);
          }
        }}
        positioning={{
          placement: 'bottom-end',
          strategy: 'fixed',
          gutter: 8,
          getAnchorRect: () =>
            triggerAnchorRef.current?.getBoundingClientRect() ?? null,
        }}
        closeOnInteractOutside
        onInteractOutside={(event) => {
          if (document.querySelector(NESTED_DISMISSABLE_LAYER_SELECTOR)) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          if (document.querySelector(NESTED_DISMISSABLE_LAYER_SELECTOR)) {
            event.preventDefault();
          }
        }}
      >
        <Tooltip title={triggerLabel}>
          <Popover.Anchor asChild>
            <Box ref={triggerAnchorRef} display="inline-flex">
              <MenuWithItems
                items={menuItems}
                positioning={{ strategy: 'fixed', placement: 'bottom-end' }}
                trigger={
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={isDisabled}
                    aria-label={triggerLabel}
                    position="relative"
                  >
                    <Box position="relative" display="inline-flex">
                      <Bug02Icon size={16} />
                      {badgeCount > 0 ? (
                        <Box
                          position="absolute"
                          top="-6px"
                          right="-8px"
                          minW={3.5}
                          h={3.5}
                          px="1"
                          borderRadius="full"
                          bg="bg.accent.default"
                          color="fg.default"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="2xs"
                          fontWeight="bold"
                          lineHeight="1"
                        >
                          {badgeCount}
                        </Box>
                      ) : null}
                      {hasTriggerUnseen && badgeCount === 0 ? (
                        <Box
                          position="absolute"
                          top="-1px"
                          right="-2px"
                          w="2"
                          h="2"
                          borderRadius="full"
                          bg="bg.status.resolved"
                        />
                      ) : null}
                    </Box>
                  </Button>
                }
              />
            </Box>
          </Popover.Anchor>
        </Tooltip>

        <Popover.Positioner>
          <Popover.Content
            w="430px"
            maxW="430px"
            maxH="80vh"
            overflowY="auto"
            bg="bg.default"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="border.muted"
            boxShadow="xl"
            p="0"
          >
            {panel === 'ai-discovery' ? (
              <ErrorBoundary
                scopeId={`ai-discovery-monitor-${applicationId ?? 'none'}`}
              >
                {applicationId ? (
                  <AiDiscoveryMonitorPanel
                    applicationId={applicationId}
                    hasHistoryUnseen={hasHistoryUnseen}
                    onOpenHistory={openHistory}
                    onClose={() => setPanel(null)}
                  />
                ) : (
                  <VStack gap="2" py="4" px="3">
                    <BodyDefault color="fg.muted">
                      {tDiscovery('noTarget')}
                    </BodyDefault>
                  </VStack>
                )}
              </ErrorBoundary>
            ) : null}

            {panel === 'crawler' ? (
              <ErrorBoundary
                scopeId={`crawler-monitor-${applicationId ?? 'none'}`}
              >
                {applicationId ? (
                  <CrawlerMonitorPanel
                    applicationId={applicationId}
                    onOpenHistory={openHistory}
                    onClose={() => setPanel(null)}
                  />
                ) : (
                  <VStack gap="2" py="4" px="3">
                    <BodyDefault color="fg.muted">
                      {t('selectTarget')}
                    </BodyDefault>
                  </VStack>
                )}
              </ErrorBoundary>
            ) : null}

            {panel === 'directory-scan' && showDirectoryScan ? (
              <ErrorBoundary
                scopeId={`directory-scan-${applicationId ?? 'none'}`}
              >
                {applicationId ? (
                  <DirectoryScanPanel
                    applicationId={applicationId}
                    onClose={() => setPanel(null)}
                  />
                ) : (
                  <VStack gap="2" py="4" px="3">
                    <BodyDefault color="fg.muted">
                      {tDiscovery('noTarget')}
                    </BodyDefault>
                  </VStack>
                )}
              </ErrorBoundary>
            ) : null}

            {panel === 'deep-security-scan' && showDeepSecurityScan ? (
              <ErrorBoundary
                scopeId={`deep-security-scan-${applicationId ?? 'none'}`}
              >
                {applicationId ? (
                  <DeepSecurityScanPanel
                    applicationId={applicationId}
                    onClose={() => setPanel(null)}
                  />
                ) : (
                  <VStack gap="2" py="4" px="3">
                    <BodyDefault color="fg.muted">
                      {tDiscovery('noTarget')}
                    </BodyDefault>
                  </VStack>
                )}
              </ErrorBoundary>
            ) : null}
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>

      {applicationId && isAiApplication ? (
        <ErrorBoundary
          scopeId={`ai-discovery-history-${applicationId}`}
          onClose={() => setIsHistoryOpen(false)}
        >
          <AiDiscoveryHistoryDrawer
            applicationId={applicationId}
            isOpen={isHistoryOpen}
            onClose={() => setIsHistoryOpen(false)}
          />
        </ErrorBoundary>
      ) : null}

      {applicationId && !isAiApplication ? (
        <ErrorBoundary
          scopeId={`crawler-history-${applicationId}`}
          onClose={() => setIsHistoryOpen(false)}
        >
          <CrawlerHistoryDrawer
            applicationId={applicationId}
            isOpen={isHistoryOpen}
            onClose={() => setIsHistoryOpen(false)}
          />
        </ErrorBoundary>
      ) : null}
    </>
  );
}
